import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import InvoicePreview from '../shared/InvoicePreview';
import { formatAmountLocale } from '../../utils/amountUtils';
import { determineProAccess, canAccessChat } from '../../utils/proAccess';
import { useUnreadCount } from '../../hooks/useUnreadCount';
import api from '../../utils/api';
import { downloadInvoice } from '../../utils/invoiceDownload';
import { buildContactFromSources, normalizeContactDetails } from '../../utils/contactNormalization';
import {
  Search,
  MessageSquare,
  Send,
  Paperclip,
  Trash2,
  Pin,
  Archive,
  Eye,
  Download,
  FileText,
  FileSpreadsheet,
  MoreVertical,
  User,
  Clock,
  Check,
  CheckCheck,
  AlertCircle,
  Plus,
  Filter,
  X,
  Loader2
} from 'lucide-react';
import PlanBadge from '../common/PlanBadge';

const Chat = () => {
  const { user } = useAuth();
  const { markChatAsRead, markChatAsVisited } = useUnreadCount();
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [showNewChat, setShowNewChat] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [userSubscription, setUserSubscription] = useState('Essential');
  const [availableInvoices, setAvailableInvoices] = useState([]);
  const [newChatInvoiceId, setNewChatInvoiceId] = useState('');
  const [newChatMessage, setNewChatMessage] = useState('');
  const [creatingChat, setCreatingChat] = useState(false);
  const [invoiceSearchQuery, setInvoiceSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedInvoiceForChat, setSelectedInvoiceForChat] = useState(null);
  const [isGuestUser, setIsGuestUser] = useState(false);
  const [showConversationMenu, setShowConversationMenu] = useState(false);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [showInvoicePreview, setShowInvoicePreview] = useState(false);
  const fileInputRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const isSendingMessage = useRef(false);
  const [isDragging, setIsDragging] = useState(false);

  // Determine account scope: additional users act on behalf of parent account
  const accountUserId = useMemo(() => {
    const role = String(user?.role || '').toLowerCase();
    const parentId = user?.parentUserId || user?.parent_user_id;
    return role === 'additional_user' && (parentId) ? parentId : user?.id;
  }, [user?.role, user?.parentUserId, user?.parent_user_id, user?.id]);

  const currentUserId = user?.id ? String(user.id) : null;
  const accountOwnerId = accountUserId ? String(accountUserId) : null;

  const normalizeMessage = useCallback((message) => {
    if (!message) return null;

    const sender =
      message.sender ||
      message.user ||
      {};

    const senderId =
      message.senderId ||
      message.sender_id ||
      sender.id ||
      message.userId ||
      message.user_id ||
      null;

    const parentUserId =
      sender.parentUserId ||
      sender.parent_user_id ||
      message.parentUserId ||
      message.parent_user_id ||
      null;

    const senderEmail =
      message.senderEmail ||
      sender.email ||
      message.email ||
      null;

    const senderType = message.senderType || sender.type || null;
    const direction = message.direction || null;

    const content = message.content || message.message || message.text || '';
    const createdAt =
      message.createdAt ||
      message.created_at ||
      message.timestamp ||
      message.sentAt ||
      new Date().toISOString();

    return {
      ...message,
      sender: {
        ...sender,
        parentUserId
      },
      senderId,
      senderParentId: parentUserId,
      senderEmail,
      senderType,
      direction,
      content,
      text: content,
      timestamp: message.timestamp || createdAt,
      createdAt,
      attachments: Array.isArray(message.attachments) ? message.attachments : [],
      isRead: message.isRead ?? message.is_read ?? false
    };
  }, []);

  // Mark conversation as read
  const markAsRead = (conversationId) => {
    markChatAsRead(conversationId);
    // Update local state to remove animation immediately
    setConversations(prev => 
      prev.map(conv => 
        conv.id === conversationId ? { ...conv, unreadCount: 0 } : conv
      )
    );
  };

  // Helper function to check if a message is recent (within last 5 minutes)
  const isRecentMessage = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = (now - date) / (1000 * 60);
    return diffInMinutes < 5; // Messages from last 5 minutes
  };

  // Helper function to format timestamps
  const formatTimestamp = (timestamp) => {
    console.log('🕐 formatTimestamp called with:', timestamp, 'type:', typeof timestamp);
    if (!timestamp) {
      console.log('❌ Timestamp is falsy:', timestamp);
      return 'Unknown time';
    }
    
    const date = new Date(timestamp);
    console.log('🕐 Parsed date:', date, 'isValid:', !isNaN(date.getTime()));
    if (isNaN(date.getTime())) return 'Invalid time';
    
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);
    const diffInMinutes = (now - date) / (1000 * 60);
    const diffInDays = Math.floor(diffInHours / 24);

    // Show relative time for all messages
    if (diffInMinutes < 1) {
      return 'Just now';
    } else if (diffInMinutes < 60) {
      return `${Math.floor(diffInMinutes)}m ago`;
    } else if (diffInHours < 24) {
      const hours = Math.floor(diffInHours);
      return `${hours}h ago`;
    } else if (diffInDays === 1) {
      return 'Yesterday';
    } else if (diffInDays < 7) {
      return `${diffInDays}d ago`;
    } else if (diffInDays < 30) {
      const weeks = Math.floor(diffInDays / 7);
      return `${weeks}w ago`;
    } else if (diffInDays < 365) {
      const months = Math.floor(diffInDays / 30);
      return `${months}mo ago`;
    } else {
      const years = Math.floor(diffInDays / 365);
      return `${years}y ago`;
    }
  };

  // Helper function to get status colors
  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      paid: 'bg-emerald-100 text-emerald-800',
      disputed: 'bg-orange-100 text-orange-800'
    };
    return colors[status] || colors.pending;
  };

  // Memoize messages to prevent unnecessary re-renders
  const memoizedMessages = useMemo(() => {
    
    if (!activeConversation?.messages) return [];
    
    const otherIdentifiers = new Set();
    const otherEmails = new Set();

    const registerParticipant = (participant) => {
      if (!participant) return;
      const id = participant.id || participant.participantId || participant.userId;
      const parentId = participant.parentUserId || participant.parent_user_id;
      const email = participant.email || participant.emailAddress;
      if (id) otherIdentifiers.add(String(id));
      if (parentId) otherIdentifiers.add(String(parentId));
      if (email) otherEmails.add(String(email).toLowerCase());
    };

    registerParticipant(activeConversation.otherParticipant);
    registerParticipant(activeConversation.customer);
    registerParticipant(activeConversation.otherParty);

    if (Array.isArray(activeConversation.participants)) {
      activeConversation.participants.forEach(registerParticipant);
    }

    const processedMessages = activeConversation.messages
      .map(normalizeMessage)
      .filter(Boolean)
      .map((message, index) => {
        const senderId = message.senderId ? String(message.senderId) : null;
        const senderParentId = message.senderParentId ? String(message.senderParentId) : null;
        const senderEmail = message.senderEmail ? String(message.senderEmail).toLowerCase() : null;
        const senderType = message.senderType ? String(message.senderType).toLowerCase() : null;
        const direction = message.direction ? String(message.direction).toLowerCase() : null;

        const ownsAsCurrent =
          (currentUserId && senderId === currentUserId) ||
          (currentUserId && senderParentId === currentUserId);

        const ownsAsAccount =
          (accountOwnerId && senderId === accountOwnerId) ||
          (accountOwnerId && senderParentId === accountOwnerId);

        const belongsToAccount = Boolean(ownsAsAccount);

        let isOwn = Boolean(ownsAsCurrent || ownsAsAccount);

        if (!isOwn && senderEmail && user?.email) {
          const currentEmail = String(user.email).toLowerCase();
          if (senderEmail === currentEmail) {
            isOwn = true;
          }
        }

        if (!isOwn && senderType) {
          if (['account', 'internal', 'team', 'user', 'owner'].some(key => senderType.includes(key))) {
            isOwn = true;
          } else if (['customer', 'client', 'external', 'receiver'].some(key => senderType.includes(key))) {
            isOwn = false;
          }
        }

        let externalDirection = false;
        if (direction) {
          if (['incoming', 'inbound', 'received'].some(key => direction.includes(key))) {
            externalDirection = true;
          } else if (['outgoing', 'outbound', 'sent'].some(key => direction.includes(key))) {
            isOwn = true;
          }
        }

        const belongsToOther =
          (senderId && otherIdentifiers.has(senderId)) ||
          (senderParentId && otherIdentifiers.has(senderParentId)) ||
          (senderEmail && otherEmails.has(senderEmail));

        if (!isOwn && (belongsToOther || externalDirection)) {
          isOwn = false;
        } else if (!isOwn && !belongsToOther) {
          isOwn = true;
        }

        let initials = 'U';
        let displayName = message.senderName || '';

        if (isOwn) {
          if (user?.firstName && user?.lastName) {
            initials = `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
            displayName = `${user.firstName} ${user.lastName}`;
          } else if (user?.firstName) {
            initials = user.firstName[0].toUpperCase();
            displayName = user.firstName;
          } else {
            initials = 'Y';
            displayName = 'You';
          }
        } else {
          const customerInfo =
            message.sender ||
            activeConversation.otherParticipant ||
            activeConversation.customer ||
            {};
          displayName = customerInfo.companyName ||
            customerInfo.name ||
            (customerInfo.firstName && customerInfo.lastName
              ? `${customerInfo.firstName} ${customerInfo.lastName}`
              : 'Customer');
          initials = (customerInfo.companyName || customerInfo.name || 'CU')
            .substring(0, 2)
            .toUpperCase();
        }

        return {
          ...message,
          isOwnMessage: isOwn,
          isOtherParticipantInternal: belongsToAccount,
          actualSenderInitials: initials,
          displayName
        };
      });
    
    // Sort messages by timestamp (newest first, since we use flex-col-reverse)
    const sortedMessages = processedMessages.sort((a, b) => {
      const timestampA = new Date(a.timestamp).getTime();
      const timestampB = new Date(b.timestamp).getTime();
      return timestampB - timestampA; // Newest first
    });
    
    return sortedMessages;
  }, [activeConversation?.messages, user?.id, user?.role, user?.parentUserId]);

  // Close conversation menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showConversationMenu && !event.target.closest('.conversation-menu')) {
        setShowConversationMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showConversationMenu]);

  // Fetch user subscription
  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        const response = await api.get('/users/profile');
        const data = response.data;
          const plan = data.subscriptionPlan;
          const status = data.subscriptionStatus;
          console.log('🔍 Subscription plan from API:', plan, 'Status:', status);
          
          if (plan) {
            // Map backend subscription plan to frontend format
            let frontendPlan;
            switch (plan) {
              case 'pro':
                // Pro users get Pro features regardless of trial status
                frontendPlan = 'Pro';
                break;
              case 'essential':
                frontendPlan = 'Essential';
                break;
              case 'free_trial':
                frontendPlan = 'Free Trial';
                break;
              default:
                frontendPlan = 'Essential';
            }
            
            console.log('🔍 Setting userSubscription to:', frontendPlan);
            setUserSubscription(frontendPlan);
          }
      } catch (error) {
        console.error('Failed to fetch subscription in Chat:', error);
      }
    };

    fetchSubscription();
  }, []);


  // Helper function to sort conversations by last message timestamp (newest first)
  const sortConversationsByLastMessage = useCallback((conversations) => {
    return conversations.sort((a, b) => {
      const timeA = new Date(a.lastMessage.timestamp).getTime();
      const timeB = new Date(b.lastMessage.timestamp).getTime();
      return timeB - timeA; // Descending order (newest first)
    });
  }, []);


  // Fetch real chat conversations from backend
  const fetchConversations = useCallback(async (silentRefresh = false) => {
      // Skip fetching if we're currently sending a message to prevent race conditions
      if (isSendingMessage.current) {
        return;
      }
      
      try {
        // fetchConversations called
        // Only show loading spinner on initial load, not on background polling
        if (!silentRefresh) {
          setLoading(true);
        }
        
        const response = await api.get('/chat');
        const data = response.data;
          // API response data received
          console.log('🔍 Chat API Response:', data);
          console.log('🔍 Conversations count:', data.conversations?.length || 0);
          
          // Get user's last login time for unread detection
          const lastLoginTime = localStorage.getItem('lastLoginTime') || new Date().toISOString();
          // Using last login time for unread detection
          // Time difference calculated for unread detection
        
        // Transform backend data to frontend format
        const rawConversations = data.conversations || data.chats || [];
        console.log('🔍 Raw conversations:', rawConversations);
        
        // Debug: Check first conversation's lastMessage timestamp
        if (rawConversations.length > 0) {
          const firstConv = rawConversations[0];
          console.log('🔍 First conversation RAW data:', {
            id: firstConv.id,
            lastMessage: firstConv.lastMessage,
            lastMessageFull: JSON.stringify(firstConv.lastMessage),
            timestamp: firstConv.lastMessage?.timestamp,
            last_message_at: firstConv.last_message_at,
            createdAt: firstConv.createdAt,
            unreadCount: firstConv.unreadCount,
            messages: firstConv.messages?.length
          });
          console.log('🔍 FULL chat object keys:', Object.keys(firstConv));
          console.log('🔍 chat.lastMessage type:', typeof firstConv.lastMessage);
          console.log('🔍 chat.lastMessage keys:', firstConv.lastMessage ? Object.keys(firstConv.lastMessage) : 'null');
          console.log('🔍 chat.unreadCount:', firstConv.unreadCount, 'type:', typeof firstConv.unreadCount);
        }
        // Get last chat visit timestamp for unread calculation
        const lastChatVisitKey = `lastChatVisit_${user.id}`;
        let lastChatVisitTimestamp = localStorage.getItem(lastChatVisitKey);
        if (!lastChatVisitTimestamp) {
          // Set to 24 hours ago to see existing messages as unread
          const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
          lastChatVisitTimestamp = yesterday;
          localStorage.setItem(lastChatVisitKey, lastChatVisitTimestamp);
        }
        const lastChatVisitDate = new Date(lastChatVisitTimestamp);
        
        const transformedConversations = rawConversations.map(chat => {
          // Use backend's unreadCount which is based on is_read field in database
          // This ensures unread status persists across sessions
          const unreadCount = chat.unreadCount || 0;
          const normalizedMessages = Array.isArray(chat.messages)
            ? chat.messages.map(normalizeMessage).filter(Boolean)
            : [];

          const lastMessageSource = chat.lastMessage || chat.last_message || null;
          const fallbackLastMessage = normalizedMessages.length > 0
            ? normalizedMessages[normalizedMessages.length - 1]
            : null;

          const conversationObj = {
            id: chat.id,
            invoiceId: chat.invoiceId || chat.invoice_id,
            invoice: chat.invoice, // Add the full invoice object
            invoiceNumber: chat.invoiceNumber || chat.invoice?.invoiceNumber || 'Unknown',
            // Preserve otherParticipant from backend (includes subscriptionPlan)
            otherParticipant: chat.otherParticipant || null,
            customer: chat.customer || {
              id: chat.otherParticipant?.id || chat.participant2_id,
              name: chat.otherParticipant?.name || 'Unknown Company',
              email: chat.otherParticipant?.email || 'unknown@email.com',
              avatar: chat.otherParticipant?.name?.substring(0, 2).toUpperCase() || 'UN',
              subscriptionPlan: chat.otherParticipant?.subscriptionPlan
            },
            lastMessage: lastMessageSource
              ? {
                  text: lastMessageSource.text || lastMessageSource.content || chat.last_message_text || 'No messages yet',
                  timestamp: lastMessageSource.timestamp || chat.last_message_at || chat.createdAt || new Date().toISOString(),
                  senderId: lastMessageSource.senderId || lastMessageSource.sender_id,
                  senderName:
                    lastMessageSource.senderName ||
                    (lastMessageSource.sender?.firstName && lastMessageSource.sender?.lastName
                      ? `${lastMessageSource.sender.firstName} ${lastMessageSource.sender.lastName}`
                      : 'Unknown')
                }
              : fallbackLastMessage
              ? {
                  text: fallbackLastMessage.content || fallbackLastMessage.text || 'No messages yet',
                  timestamp: fallbackLastMessage.timestamp || fallbackLastMessage.createdAt || new Date().toISOString(),
                  senderId: fallbackLastMessage.senderId,
                  senderName: fallbackLastMessage.senderName || 'Unknown'
                }
              : {
                  text: 'No messages yet',
                  timestamp: chat.last_message_at || chat.createdAt || new Date().toISOString(),
                  senderId: null,
                  senderName: 'Unknown'
                },
            unreadCount: unreadCount, // Use backend's unreadCount from database
            isPinned: chat.metadata?.isPinned || false,
            isArchived: chat.metadata?.isArchived || false,
            status: chat.status || chat.invoice?.status || 'pending',
            amount: chat.amount || chat.invoice?.total || 0,
            currency: chat.currency || chat.invoice?.currency || 'USD',
            dueDate: chat.dueDate || chat.invoice?.dueDate || null,
            messages: normalizedMessages, // Will be loaded when conversation is selected
            isDirectMessage: chat.isDirectMessage !== undefined ? chat.isDirectMessage : !chat.invoiceId // Use Lambda's value or fallback
          };
          
          // Compute otherParty based on account scope (additional_user uses parent account)
          try {
            const invoice = conversationObj.invoice;
            const senderId = invoice?.senderId || invoice?.sender_id;
            const receiverId = invoice?.receiverId || invoice?.receiver_id;
            let otherPartyId = null;
            if (senderId && receiverId) {
              otherPartyId = senderId === accountUserId ? receiverId : receiverId === accountUserId ? senderId : null;
            }
            if (otherPartyId && invoice) {
              const otherParty = senderId === otherPartyId ? (invoice.sender || {}) : (invoice.receiver || {});
              // Replace customer info with other party (end user)
              // Use subscriptionPlan from invoice if available, otherwise fallback to otherParticipant
              conversationObj.customer = {
                id: otherParty.id || otherPartyId,
                name: otherParty.companyName || [otherParty.firstName, otherParty.lastName].filter(Boolean).join(' ') || conversationObj.customer?.name,
                email: otherParty.email || conversationObj.customer?.email,
                avatar: (otherParty.companyName || otherParty.firstName)?.substring(0,2)?.toUpperCase() || conversationObj.customer?.avatar || 'UN',
                subscriptionPlan: otherParty.subscriptionPlan || conversationObj.otherParticipant?.subscriptionPlan || conversationObj.customer?.subscriptionPlan
              };
            }
          } catch (_) {}
          
          return conversationObj;
        });

        // Preserve data from EXISTING state (conversations) to prevent loss during refresh
        const mergedConversations = transformedConversations.map(newConv => {
          const existingConv = conversations.find(c => c.id === newConv.id);
          console.log('🔄 Merging conversation:', {
            id: newConv.id,
            hasExisting: !!existingConv,
            newTimestamp: newConv.lastMessage?.timestamp,
            existingTimestamp: existingConv?.lastMessage?.timestamp,
            newMessages: newConv.messages?.length,
            existingMessages: existingConv?.messages?.length
          });
          if (existingConv) {
            // Preserve messages if they exist
            if (existingConv.messages && existingConv.messages.length > 0) {
              console.log('✅ Preserving', existingConv.messages.length, 'messages from existing');
              newConv.messages = existingConv.messages;
            }
            // Preserve lastMessage.timestamp if it exists and is valid
            if (existingConv.lastMessage?.timestamp && !newConv.lastMessage?.timestamp) {
              console.log('✅ Preserving timestamp from existing:', existingConv.lastMessage.timestamp);
              newConv.lastMessage = {
                ...newConv.lastMessage,
                timestamp: existingConv.lastMessage.timestamp
              };
            }
          }
          return newConv;
        });
        
        // Also preserve messages for the currently active conversation
        if (activeConversation && activeConversation.messages && activeConversation.messages.length > 0) {
          const activeConvIndex = mergedConversations.findIndex(conv => conv.id === activeConversation.id);
          if (activeConvIndex !== -1) {
            mergedConversations[activeConvIndex].messages = activeConversation.messages;
            // Preserve the lastMessage timestamp from activeConversation if it exists
            if (activeConversation.lastMessage?.timestamp) {
              mergedConversations[activeConvIndex].lastMessage = {
                ...mergedConversations[activeConvIndex].lastMessage,
                timestamp: activeConversation.lastMessage.timestamp
              };
            }
          }
        }

        
        // Filter out any self-conversations (where customer is the same as account)
        const filtered = mergedConversations.filter(conv => conv.customer?.id && conv.customer.id !== accountUserId);
        // Sort conversations by last message timestamp (newest first)
        const conversationsWithRealTimestamps = sortConversationsByLastMessage(filtered);
        console.log('🔍 Final conversations to set:', conversationsWithRealTimestamps);

          setConversations(conversationsWithRealTimestamps);
          
          // Dispatch event to update unread count when conversations are refreshed
          window.dispatchEvent(new CustomEvent('newMessageReceived'));
          
          // Update active conversation if it exists to preserve messages
          if (activeConversation) {
            const updatedActiveConv = conversationsWithRealTimestamps.find(conv => conv.id === activeConversation.id);
            if (updatedActiveConv) {
              setActiveConversation(updatedActiveConv);
            }
          }
          
          // Update sidebar unread count via custom event
          const totalUnread = conversationsWithRealTimestamps.reduce((sum, conv) => sum + conv.unreadCount, 0);
          window.dispatchEvent(new CustomEvent('updateUnreadCount', { detail: { unreadCount: totalUnread } }));
      } catch (error) {
        console.error('Error fetching conversations:', error);
      } finally {
        setLoading(false);
      }
    }, []);

  // Set last login time when component mounts (only once per session)
  useEffect(() => {
    if (!user?.id) return;
    
    const sessionKey = `chatLoginTime_${user.id}`;
    
    // Check if this is a new session for this user
    const sessionLoginTime = sessionStorage.getItem(sessionKey);
    
    if (!sessionLoginTime) {
      // New session - set login time to 24 hours ago to show recent messages as new
      const loginTime = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
      localStorage.setItem('lastLoginTime', loginTime);
      sessionStorage.setItem(sessionKey, loginTime);
      
      // Trigger a refresh of conversations to apply the new login time
      setTimeout(() => {
        fetchConversations();
      }, 100);
    } else {
    }
  }, [user?.id, fetchConversations]);

  // Fetch conversations on mount and when user subscription changes
  useEffect(() => {
    console.log('🔍 Subscription check:', { userSubscription, userId: user?.id, userEmail: user?.email });
    
    // Use comprehensive Pro access check with safeguards
    const hasProAccess = determineProAccess(user);
    const canChat = canAccessChat(user?.subscriptionPlan, user?.subscriptionStatus, user?.role);
    
    console.log('🔍 Pro access check:', { hasProAccess, canChat, user: user?.email });
    
    if ((hasProAccess || canChat) && user?.id) {
      // Mark chat as visited when user enters Chat tab
      markChatAsVisited();
      // Small delay to ensure login time is set first
      setTimeout(() => {
      fetchConversations();
      fetchAvailableInvoices();
      }, 200);
    }
  }, [userSubscription, user?.id, user?.email, fetchConversations]);

  // Poll for new messages every 30 seconds
  useEffect(() => {
    const hasProAccess = determineProAccess(user);
    if (hasProAccess) {
      const interval = setInterval(() => {
        if (isSendingMessage.current) {
          return;
        }
        // Silent refresh - don't show loading spinner during background polling
        fetchConversations(true);
      }, 30000);
      
      return () => clearInterval(interval);
    }
  }, [user, fetchConversations]);

  // Auto-refresh messages when chat is active
  useEffect(() => {
    if (activeConversation && activeConversation.id) {
      // Poll for new messages every 15 seconds
      const interval = setInterval(async () => {
        try {
          const response = await fetch(`/api/chat/${activeConversation.id}/messages`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            }
          });
          
          if (response.ok) {
            const data = await response.json();
            const accountUserId = (user?.role === 'additional_user' && user?.parentUserId) ? user.parentUserId : user?.id;
            const companyLabel = (user?.companyName || 'Company').trim();
            const transformedMessages = (data.messages || [])
              .map(normalizeMessage)
              .filter(Boolean)
              .map(msg => ({
                ...msg,
                senderName:
                  (msg.sender?.firstName && msg.sender?.lastName)
                    ? `${msg.sender.firstName} ${msg.sender.lastName}`.trim()
                    : msg.senderName ||
                      (msg.senderId === accountUserId ? `${companyLabel} (Team)` : 'Customer')
              }));
            
            // Only update if there are new messages
            if (transformedMessages.length > activeConversation.messages.length) {
              setActiveConversation(prev => ({
                ...prev,
                messages: transformedMessages
              }));
            }
          }
        } catch (error) {
          console.error('Error refreshing messages:', error);
        }
      }, 15000); // 15 seconds
      
      return () => clearInterval(interval);
    }
  }, [activeConversation?.id]);

  // Track new messages and play notification sound
  useEffect(() => {
    if (userSubscription === 'Pro' && conversations.length > 0) {
      // Check for new unread messages
      const totalUnread = conversations.reduce((sum, conv) => sum + conv.unreadCount, 0);
      
      if (totalUnread > 0) {
        
        // Find conversations with recent unread messages
        const recentUnreadConversations = conversations.filter(conv => 
          conv.unreadCount > 0 && isRecentMessage(conv.lastMessage.timestamp)
        );
        
        if (recentUnreadConversations.length > 0) {
          
          // Play notification sound for recent messages
          try {
            const audio = new Audio('/notification.mp3');
            audio.volume = 0.3;
            audio.play().catch(() => {
              // Ignore if audio fails to play
            });
          } catch (error) {
            // Ignore audio errors
          }
        } else {
        }
      } else {
      }
    }
  }, [conversations, userSubscription]);

  // Search for invoices to start chats
  const searchInvoices = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    try {
      setSearching(true);
      
      console.log('🔍 Searching for invoices with query:', query);
      
      const url = `/invoices?search=${encodeURIComponent(query)}`;
      console.log('🔍 Calling URL:', url);
      
      const response = await api.get(url);
      console.log('Invoice search response:', response);
      
      const data = response.data;
      console.log('Invoice search response data:', data);
      
      const invoices = data.data?.invoices || data.invoices || [];
      console.log('Found invoices:', invoices.length, invoices);
      
      // Filter invoices that don't have chats yet
      const invoicesWithoutChats = invoices.filter(invoice => 
        !conversations.some(conv => conv.invoiceId === invoice.id)
      );
      
      console.log('Invoices without chats:', invoicesWithoutChats.length, invoicesWithoutChats);
      console.log('Current conversations:', conversations);
      
      setSearchResults(invoicesWithoutChats);
    } catch (error) {
      console.error('Error searching invoices:', error);
      if (error.response) {
        console.error('Error response:', error.response.data);
      }
    } finally {
      setSearching(false);
    }
  };


  // Handle invoice selection for new chat
  const handleInvoiceSelection = async (invoice) => {
    setSelectedInvoiceForChat(invoice);
    setNewChatInvoiceId(invoice.id);
    
    // Check if this is a guest user invoice by checking for GuestInvoice record
    try {
      const response = await api.get(`/invoices/${invoice.id}/guest-check`);
      const data = response.data;
      setIsGuestUser(data.isGuestUser);
    } catch (error) {
      console.error('Error checking guest user status:', error);
      setIsGuestUser(false);
    }
    
    // Clear search
    setInvoiceSearchQuery('');
    setSearchResults([]);
  };

  // Fetch available invoices for new chats
  const fetchAvailableInvoices = async () => {
    try {
      const response = await api.get('/invoices');
      const data = response.data;
      const invoices = data.data?.invoices || data.invoices || [];
      
      // Filter invoices that don't have chats yet
      const invoicesWithoutChats = invoices.filter(invoice => 
        !conversations.some(conv => conv.invoiceId === invoice.id)
      );
      
      setAvailableInvoices(invoicesWithoutChats);
    } catch (error) {
      console.error('Error fetching available invoices:', error);
    }
  };

  // Filter conversations based on search and filters
  const filteredConversations = conversations.filter(conv => {
    const matchesSearch = 
      conv.invoiceNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
      conv.customer.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      conv.customer.email.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Handle archived conversations toggle
    const isArchived = conv.metadata?.isArchived || false;
    if (showArchived) {
      // When showing archived, only show archived conversations
      if (!isArchived) return false;
    } else {
      // When showing active conversations, exclude archived ones
      if (isArchived) return false;
    }
    
    // Handle chat filters
    let matchesFilter = true;
    if (filterStatus === 'archived') {
      matchesFilter = conv.isArchived;
    } else if (filterStatus !== 'all') {
      // Status-based filters (pending, approved, paid, etc.)
      matchesFilter = conv.status === filterStatus;
    }
    
    return matchesSearch && matchesFilter;
  });

  // Sort conversations: pinned first, then by most recent message
  const sortedConversations = [...filteredConversations].sort((a, b) => {
    // First sort by pinned status (pinned conversations first)
    const aPinned = a.metadata?.isPinned || false;
    const bPinned = b.metadata?.isPinned || false;
    
    if (aPinned && !bPinned) return -1;
    if (!aPinned && bPinned) return 1;
    
    // Then sort by most recent message
    return new Date(b.lastMessage.timestamp) - new Date(a.lastMessage.timestamp);
  });

  // No auto-scroll needed - messages are positioned at bottom by default

  // Transform invoice data for InvoicePreview component
  const transformInvoiceForPreview = (invoice, conversation) => {
    if (!invoice) return null;

    const normalizedUserId = user?.id ? String(user.id) : null;
    const senderOwnerId =
      invoice.senderId ??
      invoice.sender_id ??
      invoice.userId ??
      invoice.user_id ??
      null;
    const receiverOwnerId =
      invoice.receiverId ??
      invoice.receiver_id ??
      invoice.customerId ??
      invoice.customer_id ??
      null;

    const viewerIsSender =
      normalizedUserId && senderOwnerId
        ? String(senderOwnerId) === normalizedUserId
        : false;
    const viewerIsReceiver =
      normalizedUserId && receiverOwnerId
        ? String(receiverOwnerId) === normalizedUserId
        : false;

    const viewerContact = normalizeContactDetails({
      companyName: user?.companyName,
      firstName: user?.firstName,
      lastName: user?.lastName,
      email: user?.email,
      address: user?.address,
      city: user?.city,
      state: user?.state,
      zipCode: user?.zipCode,
      country: user?.country,
      subscriptionPlan: user?.subscriptionPlan
    });

    const receiverData =
      buildContactFromSources(
        invoice.receiver,
        invoice.customer,
        invoice.metadata?.receiver,
        invoice.receiverSnapshot,
        conversation?.customer
      ) || (viewerIsReceiver ? viewerContact : null);

    const senderData =
      buildContactFromSources(
        invoice.sender,
        invoice.supplier,
        invoice.from,
        invoice.metadata?.sender,
        invoice.senderSnapshot,
        conversation?.otherParticipant
      ) || (viewerIsSender ? viewerContact : null);

    return {
      ...invoice,
      createdAt: invoice.created_at || invoice.createdAt,
      dueDate: invoice.dueDate,
      paymentDate: invoice.paymentDate,
      lineItems: Array.isArray(invoice.items) ? invoice.items : (invoice.lineItems || []),
      amount: parseFloat(invoice.total || 0),
      subtotal: parseFloat(invoice.subtotal || 0),
      tax: parseFloat(invoice.tax || 0),
      receiver: receiverData,
      sender: senderData
    };
  };

  // Conversation actions
  const handleArchiveConversation = async (conversationId) => {
    try {
      const response = await fetch(`/api/chat/${conversationId}/archive`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        
        // Refresh conversations to show updated status
        fetchConversations();
        
        // Show success message
        alert(result.message);
      } else {
        const errorData = await response.json();
        console.error('Failed to archive conversation:', errorData);
        alert('Failed to archive conversation. Please try again.');
      }
    } catch (error) {
      console.error('Error archiving conversation:', error);
      alert('Error archiving conversation. Please try again.');
    }
  };

  const handlePinConversation = async (conversationId) => {
    try {
      const response = await fetch(`/api/chat/${conversationId}/pin`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        
        // Refresh conversations to show updated status
        fetchConversations();
        
        // Show success message
        alert(result.message);
      } else {
        const errorData = await response.json();
        console.error('Failed to pin conversation:', errorData);
        alert('Failed to pin conversation. Please try again.');
      }
    } catch (error) {
      console.error('Error pinning conversation:', error);
      alert('Error pinning conversation. Please try again.');
    }
  };

  const handleDeleteConversation = async (conversationId) => {
    if (window.confirm('Are you sure you want to delete this conversation? This action cannot be undone.')) {
      try {
        const response = await api.delete(`/api/chat/${conversationId}`);
        
        if (response.data.success) {
          // Clear active conversation if it was deleted
          if (activeConversation && activeConversation.id === conversationId) {
            setActiveConversation(null);
          }
          
          // Refresh conversations to remove deleted conversation
          fetchConversations();
          
          // Show success message
          alert(response.data.message);
        } else {
          console.error('Failed to delete conversation:', response.data);
          alert('Failed to delete conversation. Please try again.');
        }
      } catch (error) {
        console.error('Error deleting conversation:', error);
        alert('Failed to delete conversation. Please try again.');
      }
    }
  };

  const handleSendMessage = async () => {
    
    if (!newMessage.trim() && attachments.length === 0) {
      return;
    }
    if (!activeConversation) {
      return;
    }

    try {
      setLoading(true);
      isSendingMessage.current = true; // Set flag to prevent fetchConversations interference
      
      // Send message to backend (POST /api/chat with invoiceId and message)
      // If there's no text but there are attachments, send a placeholder message
      const messageText = newMessage.trim() || (attachments.length > 0 ? '[File attachment]' : '');
      
      const response = await api.post('/api/chat', {
        invoiceId: activeConversation.invoiceId,
        message: messageText,
        attachments: attachments.map(att => ({
          name: att.name,
          size: att.size,
          type: att.type,
          s3Key: att.s3Key
        }))
      });

      const data = response.data;

      if (data.success) {
        // Update local state with the new message
        const optimisticMessage = {
          id: data.chatMessage?.id || Date.now(),
          content: data.chatMessage?.content || messageText,
          text: data.chatMessage?.content || messageText,
          senderId: data.chatMessage?.senderId || accountUserId,
          sender: {
            id: accountUserId,
            firstName: user?.firstName,
            lastName: user?.lastName,
            companyName: user?.companyName,
            parentUserId: user?.parentUserId
          },
          senderName: `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || 'You',
          timestamp: data.chatMessage?.createdAt || new Date().toISOString(),
          createdAt: data.chatMessage?.createdAt || new Date().toISOString(),
          attachments: attachments
        };

        const newMessageObj = normalizeMessage(optimisticMessage);

        const updatedConversation = {
          ...activeConversation,
          messages: [...activeConversation.messages, newMessageObj],
          lastMessage: {
            text: newMessageObj.content || newMessageObj.text,
            timestamp: newMessageObj.timestamp || newMessageObj.createdAt,
            senderId: newMessageObj.senderId,
            senderName: newMessageObj.senderName
          }
        };


        // Update conversations and sort by last message timestamp (newest first)
        setConversations(prev => {
          const updated = prev.map(conv => 
            conv.id === activeConversation.id ? updatedConversation : conv
          );
          return sortConversationsByLastMessage(updated);
        });
        setActiveConversation(updatedConversation);

        setNewMessage('');
        setAttachments([]);
        
        // Dispatch event to update unread count
        window.dispatchEvent(new CustomEvent('messageSent'));
        
        // Add a small delay before allowing fetchConversations to resume
        // This ensures the backend has processed the message
        setTimeout(() => {
          isSendingMessage.current = false;
        }, 5000); // Increased delay to 5 seconds
        
        // Don't refetch immediately - the local state update should be sufficient
        // The message will be included in the next natural refresh
      } else {
        console.error('Failed to send message:', data);
        alert('Failed to send message. Please try again.');
        isSendingMessage.current = false; // Clear flag on API error
      }
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Error sending message. Please try again.');
      isSendingMessage.current = false; // Clear flag on error
    } finally {
      setLoading(false);
      // Note: isSendingMessage flag is cleared in setTimeout after successful send
      // or immediately in case of error in the catch block
    }
  };

  const handleCreateNewChat = async () => {
    // Require invoice and message for invoice-based chat
    if (!newChatInvoiceId || !newChatMessage.trim()) {
      alert('Please select an invoice and enter a message');
      return;
    }
    
    // Check if this is a guest user invoice
    if (isGuestUser) {
      alert('Cannot start chat with guest users. Guest users do not have access to the chat system.');
      return;
    }
    
    // Check if chat already exists for this invoice
    const existingChat = conversations.find(conv => conv.invoiceId === newChatInvoiceId);
    if (existingChat) {
      alert('A chat already exists for this invoice. Please use the existing chat instead.');
      return;
    }

    // Get participantId from selectedInvoiceForChat
    // The other participant is:
    // - If current user is the SENDER → chat with RECEIVER
    // - If current user is the RECEIVER → chat with SENDER
    if (!selectedInvoiceForChat) {
      alert('Cannot create chat: Please select a valid invoice.');
      return;
    }
    
    const isSender = selectedInvoiceForChat.senderId === accountUserId || selectedInvoiceForChat.sender?.id === accountUserId;
    const participantId = isSender 
      ? (selectedInvoiceForChat.receiverId || selectedInvoiceForChat.receiver?.id)
      : (selectedInvoiceForChat.senderId || selectedInvoiceForChat.sender?.id);
    
    if (!participantId) {
      alert('Cannot create chat: Invalid invoice data. Missing sender or receiver information.');
      console.error('Invoice data:', selectedInvoiceForChat);
      return;
    }
    
    console.log('🔍 Creating chat:', {
      currentUser: user.id,
      isSender,
      participantId,
      invoice: selectedInvoiceForChat.invoiceNumber
    });

    try {
      setCreatingChat(true);
      
      // Create new invoice-based chat
      const response = await api.post('/api/chat', {
        invoiceId: newChatInvoiceId,
        participantId: participantId,
        initialMessage: newChatMessage.trim()
      });
      
      const data = response.data;

      // Check if this is an existing chat or a new one
      if (data.message && data.message.includes('Chat already exists')) {
        // Existing chat - just refresh and open it
        fetchConversations();
        fetchAvailableInvoices();
        
        // Reset form and close modal
        setNewChatInvoiceId('');
        setNewChatMessage('');
        setSelectedInvoiceForChat(null);
        setIsGuestUser(false);
        setShowNewChat(false);
        
        // Show info message
        alert('Chat already exists! Opening existing conversation.');
      } else {
        // New chat created
        // Refresh conversations and available invoices
        fetchConversations();
        fetchAvailableInvoices();
        
        // Reset form and close modal
        setNewChatInvoiceId('');
        setNewChatMessage('');
        setSelectedInvoiceForChat(null);
        setIsGuestUser(false);
        setShowNewChat(false);
        
        // Show success message
        alert('New chat created successfully!');
      }
    } catch (error) {
      console.error('Error creating chat:', error);
      if (error.response) {
        console.error('Failed to create chat:', error.response.status, error.response.data);
        
        if (error.response.data?.code === 'GUEST_USER_CHAT_NOT_ALLOWED') {
          alert('Cannot start chat with guest users. Guest users do not have access to the chat system.');
        } else {
          alert('Failed to create chat. Please try again.');
        }
      } else {
        alert('Error creating chat. Please try again.');
      }
    } finally {
      setCreatingChat(false);
    }
  };

  const handleDeleteMessage = async (messageId) => {
    try {
      // Delete message from backend
      await api.delete(`/api/chat/${activeConversation.id}/messages/${messageId}`);

      if (activeConversation) {
        const updatedMessages = activeConversation.messages.filter(msg => msg.id !== messageId);
        const updatedConversation = {
          ...activeConversation,
          messages: updatedMessages
        };

        setConversations(prev => 
          prev.map(conv => 
            conv.id === activeConversation.id ? updatedConversation : conv
          )
        );
        setActiveConversation(updatedConversation);
      }
    } catch (error) {
      console.error('Error deleting message:', error);
    }
  };

  const togglePin = (conversationId) => {
        setConversations(prev => 
          prev.map(conv => 
        conv.id === conversationId 
          ? { ...conv, isPinned: !conv.isPinned }
              : conv
          )
        );
  };

  const toggleArchive = (conversationId) => {
        setConversations(prev => 
          prev.map(conv => 
        conv.id === conversationId 
          ? { ...conv, isArchived: !conv.isArchived }
          : conv
      )
    );
  };


  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    
    for (const file of files) {
      try {
        // Validate file size (10MB max)
        if (file.size > 10 * 1024 * 1024) {
          alert(`${file.name} is too large. Maximum file size is 10MB.`);
          continue;
        }
        
        // Request upload URL from backend
        const uploadResponse = await api.post('/api/files/upload-url', {
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size,
          category: 'chat'
        });
        
        if (!uploadResponse.data.success) {
          alert(`Failed to prepare upload for ${file.name}`);
          continue;
        }
        
        const { uploadUrl, fileKey } = uploadResponse.data;
        
        // Upload file directly to S3
        await fetch(uploadUrl, {
          method: 'PUT',
          body: file,
          headers: {
            'Content-Type': file.type
          }
        });
        
        // Add to attachments with S3 key
        const newAttachment = {
          id: `att-${Date.now()}-${Math.random()}`,
          name: file.name,
          size: file.size,
          type: file.type,
          s3Key: fileKey,
          uploadedAt: new Date().toISOString()
        };
        
        setAttachments(prev => [...prev, newAttachment]);
        
      } catch (error) {
        console.error('Error uploading file:', error);
        alert(`Failed to upload ${file.name}. Please try again.`);
      }
    }
  };

  const removeAttachment = (attachmentId) => {
    setAttachments(prev => prev.filter(att => att.id !== attachmentId));
  };

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set dragging to false if we're leaving the drop zone entirely
    if (e.currentTarget === e.target) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    
    // Create a synthetic event to reuse handleFileUpload
    const syntheticEvent = {
      target: {
        files: files
      }
    };
    
    await handleFileUpload(syntheticEvent);
  };

  const handleDownload = async (type, invoice, extraOptions = {}) => {
    try {
      await downloadInvoice(
        invoice,
        type === 'excel' ? 'csv' : type,
        { setLoading, ...extraOptions }
      );
    } catch (error) {
      console.error(`Error downloading ${type}:`, error);
      alert(`Error downloading ${type}. Please try again.`);
    }
  };

  const handleFileAttachmentDownload = async (attachment) => {
    try {
      console.log('📥 Downloading file attachment:', attachment);
      
      // Get download URL from backend with original filename
      const response = await api.get(`/api/files/download/${encodeURIComponent(attachment.s3Key)}?filename=${encodeURIComponent(attachment.name)}`);
      
      if (response.data.success) {
        // Create a temporary link to trigger download with original filename
        const link = document.createElement('a');
        link.href = response.data.downloadUrl;
        link.download = attachment.name; // Use original filename
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        alert('Failed to download file. Please try again.');
      }
    } catch (error) {
      console.error('Error downloading file attachment:', error);
      alert('Failed to download file. Please try again.');
    }
  };




  const getStatusIcon = (status) => {
    const icons = {
      pending: Clock,
      approved: Check,
      rejected: X,
      paid: Check,
      disputed: AlertCircle
    };
    return icons[status] || Clock;
  };

  const formatCurrency = (amount) => {
    // Database stores amounts in pounds as DECIMAL(10,2)
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No due date';
    try {
      return new Date(dateString).toLocaleDateString('en-GB');
    } catch (error) {
      return 'Invalid date';
    }
  };

    return (
    <div className="space-y-4">
      {/* Chat Interface */}
      <div className="flex bg-gray-50 rounded-lg border" style={{ height: '600px' }}>
        {/* Left Panel - Conversations List */}
        <div className="w-80 bg-white border-r border-gray-200 flex flex-col rounded-l-lg">
          {/* Header */}
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <h2 className="text-lg font-semibold text-gray-900">
                  {showArchived ? 'Archived Messages' : 'Messages'}
                </h2>
                {showArchived && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                    <Archive className="w-3 h-3 mr-1" />
                    Archived
                  </span>
                )}
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => {
                    fetchConversations();
                  }}
                  className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                  title="Refresh Conversations"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>
                <button
                  onClick={() => setShowArchived(!showArchived)}
                  className={`p-2 rounded-lg transition-colors duration-200 ${
                    showArchived 
                      ? 'text-orange-600 bg-orange-50 hover:bg-orange-100' 
                      : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
                  }`}
                  title={showArchived ? 'Show Active Conversations' : 'Show Archived Conversations'}
                >
                  <Archive className="w-5 h-5" />
                </button>
                {!showArchived && (
                  <button
                    onClick={() => {
                      console.log('New Chat button clicked, current state:', showNewChat);
                      setShowNewChat(!showNewChat);
                      console.log('New Chat state will be:', !showNewChat);
                    }}
                    className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors duration-200 border border-blue-200 bg-blue-50"
                    title="New Chat"
                  >
                    <Plus className="w-5 h-5" />
                  </button>
                )}
              </div>
            </div>

            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search conversations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
            </div>

            {/* Filters */}
            <div className="mt-3 flex items-center space-x-2">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center space-x-1 px-3 py-1 text-xs text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors duration-200"
              >
                <Filter className="w-3 h-3" />
                <span>Filter</span>
              </button>
              {showFilters && (
                <div className="flex space-x-2">
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="text-xs border border-gray-300 rounded-md px-2 py-1 focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                >
                    <option value="all">All Chats</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                  <option value="paid">Paid</option>
                  <option value="disputed">Disputed</option>
                  <option value="archived">Archived</option>
                </select>
                </div>
              )}
            </div>
          </div>

          {/* Conversations List */}
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex-1 flex items-center justify-center p-8">
                <div className="text-center">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-3" />
                  <p className="text-gray-600">Loading conversations...</p>
                </div>
              </div>
            ) : sortedConversations.length === 0 ? (
              <div className="flex-1 flex items-center justify-center p-8">
                <div className="text-center">
                  {showArchived ? (
                    <>
                      <Archive className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">No archived conversations</h3>
                      <p className="text-gray-500 mb-4">You haven't archived any conversations yet.</p>
                      <button
                        onClick={() => setShowArchived(false)}
                        className="text-blue-600 hover:text-blue-800 font-medium"
                      >
                        View active conversations
                      </button>
                    </>
                  ) : (
                    <>
                      <MessageSquare className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">No conversations</h3>
                      <p className="text-gray-500 mb-4">Start a new conversation to begin messaging.</p>
                      <button
                        onClick={() => setShowNewChat(true)}
                        className="text-blue-600 hover:text-blue-800 font-medium"
                      >
                        Start new chat
                      </button>
                    </>
                  )}
              </div>
              </div>
            ) : (
              sortedConversations.map((conversation) => (
              <div
                key={conversation.id}
                onClick={async () => {
                  // Load messages for this conversation (avoid interim render to prevent flicker)
                  setIsLoadingMessages(true);
                  try {
                    const response = await api.get(`/api/chat/${conversation.id}/messages`);
                    const payload = response?.data || {};

                    // Transform backend messages to frontend format
                    console.log('📨 Messages from API:', payload.messages);
                    const transformedMessages = (payload.messages || [])
                      .map(normalizeMessage)
                      .filter(Boolean);

                    // Update conversation with messages and PRESERVE timestamp
                    const updatedConversation = {
                      ...conversation,
                      messages: transformedMessages,
                      // ALWAYS preserve the existing lastMessage with its timestamp
                      lastMessage: conversation.lastMessage
                    };

                    setActiveConversation(updatedConversation);
                    setConversations(prev => 
                      prev.map(conv => 
                        conv.id === conversation.id ? updatedConversation : conv
                      )
                    );

                    // Mark conversation as read after loading messages (localStorage + UI)
                    markAsRead(conversation.id);

                    // Mark messages as read in the database
                    try {
                      await api.post(`/api/chat/${conversation.id}/messages/mark-read`);
                    } catch (dbError) {
                      console.error('Error marking messages as read in database:', dbError);
                      // Don't break the flow if this fails
                    }
                  } catch (error) {
                    console.error('Error loading messages:', error);
                    // Fallback: still set active to show header, without messages
                    setActiveConversation(conversation);
                  } finally {
                    setIsLoadingMessages(false);
                  }
                }}
                className={`p-4 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors duration-200 ${
                  activeConversation?.id === conversation.id ? 'bg-blue-50 border-blue-200' : ''
                } ${conversation.unreadCount > 0 ? 'bg-green-50 border-l-4 border-l-green-500 font-semibold shadow-lg ring-2 ring-green-200 ring-opacity-50' : ''}`}
              >
                <div className="flex items-start space-x-3">
                  {/* Avatar */}
                  <div className="flex-shrink-0 relative">
                    <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-medium text-sm">
                      {conversation.customer.avatar}
                    </div>
                    {/* Unread Message Indicator - Green pulsing dot */}
                    {conversation.unreadCount > 0 && (
                      <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full flex items-center justify-center animate-pulse">
                        <div className="w-2 h-2 bg-white rounded-full"></div>
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <h3 className="text-sm font-medium text-gray-900 truncate">
                        {conversation.customer.name}
                      </h3>
                        {/* Pinned Indicator */}
                        {conversation.metadata?.isPinned && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            <Pin className="w-3 h-3 mr-1" />
                            Pinned
                          </span>
                        )}
                        {/* Chat Type Indicator - All chats are invoice-based */}
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          <FileText className="w-3 h-3 mr-1" />
                          Invoice
                        </span>
                            {/* Invoice Direction Indicator */}
                            {conversation.invoice && (
                              <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${
                                conversation.invoice.senderId === accountUserId 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-orange-100 text-orange-800'
                              }`}>
                                {conversation.invoice.senderId === accountUserId ? 'Sent' : 'Received'}
                              </span>
                              )}
                          </div>
                      <div className="flex items-center space-x-1">
                        {conversation.isArchived && (
                          <Archive className="w-3 h-3 text-gray-400" />
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                    <p className="text-xs text-gray-500 truncate">
                        {(() => {
                          // Expand lastMessage object to inspect its structure
                          console.log('🔍 Checking conversation:', conversation.id, 'lastMessage:', conversation.lastMessage, 'full conversation keys:', Object.keys(conversation));
                          
                          const timestamp = conversation.lastMessage?.timestamp || conversation.last_message_at;
                          const formatted = formatTimestamp(timestamp);
                          // Debug log
                          if (formatted === 'Unknown time') {
                            console.log('⚠️ Unknown time for conversation:', {
                              id: conversation.id,
                              invoiceNumber: conversation.invoiceNumber,
                              lastMessage: conversation.lastMessage,
                              timestamp: conversation.lastMessage?.timestamp,
                              last_message_at: conversation.last_message_at,
                              computedTimestamp: timestamp,
                              formatted: formatted
                            });
                          }
                          return `${conversation.invoiceNumber} • ${formatted}`;
                        })()}
                      </p>
                    </div>
                    
                    <p className={`text-sm truncate mt-1 ${
                      conversation.unreadCount > 0 
                        ? 'text-gray-900 font-medium' 
                        : 'text-gray-700'
                    }`}>
                      {conversation.lastMessage.text}
                    </p>

                    {/* Status and Actions */}
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center space-x-2">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(conversation.status)}`}>
                          {React.createElement(getStatusIcon(conversation.status), { className: 'w-3 h-3 mr-1' })}
                          {conversation.status.charAt(0).toUpperCase() + conversation.status.slice(1)}
                        </span>
                        <span className="text-xs font-medium text-gray-700">
                          {formatCurrency(conversation.amount)}
                        </span>
                      </div>
                      
                      <div className="flex items-center space-x-1">
                        {conversation.unreadCount > 0 && (
                          <span className="inline-flex items-center justify-center w-7 h-7 text-xs font-bold leading-none text-white bg-[#1F11ED] rounded-full shadow-lg ring-2 ring-blue-100">
                            {conversation.unreadCount > 99 ? '99+' : conversation.unreadCount}
                          </span>
                        )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                            togglePin(conversation.id);
                            }}
                          className="p-1 text-gray-400 hover:text-yellow-500 transition-colors duration-200"
                          title={conversation.isPinned ? 'Unpin' : 'Pin'}
                          >
                          <Pin className={`w-3 h-3 ${conversation.isPinned ? 'text-yellow-500' : ''}`} />
                          </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                            toggleArchive(conversation.id);
                                  }}
                          className="p-1 text-gray-400 hover:text-gray-600 transition-colors duration-200"
                          title={conversation.isArchived ? 'Unarchive' : 'Archive'}
                                >
                          <Archive className={`w-3 h-3 ${conversation.isArchived ? 'text-gray-600' : ''}`} />
                                </button>
                              </div>
                            </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
          </div>
        </div>

        {/* Right Panel - Active Conversation */}
        <div className="flex-1 flex flex-col bg-white rounded-r-lg">
          {activeConversation ? (
            <>
              {/* Conversation Header */}
              <div className="border-b border-gray-200 p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-medium text-sm">
                      {activeConversation.customer.avatar}
                    </div>
                    <div>
                      <div className="flex items-center space-x-2">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {activeConversation.customer.name}
                      </h3>
                        {/* Plan Badge */}
                        <PlanBadge plan={activeConversation.otherParticipant?.subscriptionPlan || activeConversation.customer?.subscriptionPlan} />
                        {/* Chat Type Badge in Header - All chats are invoice-based */}
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          <FileText className="w-3 h-3 mr-1" />
                          Invoice Chat
                        </span>
                      </div>
                      <p className="text-sm text-gray-500">{activeConversation.customer.email}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    {/* Invoice Info - All chats are invoice-based */}
                    {activeConversation.invoiceId && (
                      <>
                    <div className="text-right">
                      <p className="text-sm font-medium text-gray-900">
                            {activeConversation.invoiceNumber}
                      </p>
                      <p className="text-xs text-gray-500">
                            Due: {formatDate(activeConversation.dueDate)}
                      </p>
                      {activeConversation.invoice && (
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium mt-1 ${
                          activeConversation.invoice.senderId === accountUserId 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-orange-100 text-orange-800'
                        }`}>
                          {activeConversation.invoice.senderId === accountUserId ? 'Sent' : 'Received'}
                        </span>
                      )}
                    </div>
                    
                    {/* Status Badge */}
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(activeConversation.status)}`}>
                          {React.createElement(getStatusIcon(activeConversation.status), { className: 'w-4 h-4 mr-1' })}
                          {activeConversation.status.charAt(0).toUpperCase() + activeConversation.status.slice(1)}
                        </span>
                        </>
                      )}
                    
                    {/* Actions */}
                    <div className="flex items-center space-x-1">
                      {/* Show invoice actions for invoice-based chats */}
                      {activeConversation.invoice && (
                        <button
                          onClick={() => setShowInvoicePreview(true)}
                          className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                          title="View Invoice"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      )}
                      <div className="relative conversation-menu">
                        <button 
                          onClick={() => setShowConversationMenu(!showConversationMenu)}
                          className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                          title="Conversation options"
                        >
                          <MoreVertical className="w-4 h-4" />
                        </button>
                        
                        {/* Dropdown Menu */}
                        {showConversationMenu && (
                          <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-10">
                            <div className="py-1">
                              <button
                                onClick={() => {
                                  handlePinConversation(activeConversation.id);
                                  setShowConversationMenu(false);
                                }}
                                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                              >
                                <Pin className="w-4 h-4" />
                                <span>{activeConversation.metadata?.isPinned ? 'Unpin Conversation' : 'Pin Conversation'}</span>
                              </button>
                              <button
                                onClick={() => {
                                  handleArchiveConversation(activeConversation.id);
                                  setShowConversationMenu(false);
                                }}
                                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                              >
                                <Archive className="w-4 h-4" />
                                <span>{activeConversation.metadata?.isArchived ? 'Unarchive' : 'Archive'}</span>
                              </button>
                              <hr className="my-1" />
                              <button
                                onClick={() => {
                                  handleDeleteConversation(activeConversation.id);
                                  setShowConversationMenu(false);
                                }}
                                className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center space-x-2"
                              >
                                <Trash2 className="w-4 h-4" />
                                <span>Delete Conversation</span>
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Messages Area */}
              <div className="flex-1 overflow-y-auto p-4 flex flex-col-reverse">
                {isLoadingMessages && (
                  <div className="text-center text-gray-500 py-8">Loading messages…</div>
                )}
                {!isLoadingMessages && memoizedMessages.length === 0 && (
                  <div className="text-center text-gray-500 py-8">
                    No messages yet. Start the conversation!
                  </div>
                )}
                {!isLoadingMessages && memoizedMessages.map((message, index) => {
                  
                  return (
                  <div
                    key={message.id}
                    className={`flex ${message.isOwnMessage ? 'justify-end' : 'justify-start'} mb-4`}
                  >
                    <div className={`max-w-xs lg:max-w-md ${message.isOwnMessage ? 'order-2' : 'order-1'}`}>
                      {/* Sender name and initials for received messages */}
                      {!message.isOwnMessage && message.actualSenderInitials && (
                        <div className="flex items-center space-x-2 mb-1 ml-1">
                          <p className="text-xs font-medium text-gray-500">
                            {message.displayName}
                          </p>
                          <div 
                            className="group relative"
                            title={`Sent by ${message.displayName}`}
                          >
                            <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-medium text-gray-400 bg-gray-200 rounded-full hover:bg-gray-300 transition-colors cursor-help">
                              {message.actualSenderInitials}
                            </span>
                            {/* Tooltip */}
                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-800 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
                              Sent by {message.displayName}
                            </div>
                          </div>
                        </div>
                      )}
                      
                      <div className={`rounded-2xl px-4 py-3 relative ${
                        message.isOwnMessage
                          ? 'bg-[#1F11ED] text-white shadow-md'
                          : 'bg-gray-100 text-gray-900 border border-gray-200 shadow-sm'
                      }`}>
                        <p className="text-sm leading-relaxed">{message.text}</p>
                        
                        {/* Attachments */}
                        {message.attachments && message.attachments.length > 0 && (
                          <div className="mt-2 space-y-2">
                            {message.attachments.map((attachment) => (
                              <div 
                                key={attachment.id} 
                                className={`flex items-center justify-between p-2 rounded-lg ${
                                  message.isOwnMessage 
                                    ? 'bg-white/10 border border-white/20' 
                                    : 'bg-white border border-gray-200'
                                }`}
                              >
                                <div className="flex items-center space-x-2 flex-1 min-w-0">
                                  <FileText className={`w-4 h-4 flex-shrink-0 ${message.isOwnMessage ? 'text-white' : 'text-gray-600'}`} />
                                  <span className={`text-xs truncate ${message.isOwnMessage ? 'text-white' : 'text-gray-700'}`}>
                                    {attachment.name}
                                  </span>
                                  <span className={`text-xs flex-shrink-0 ${message.isOwnMessage ? 'text-white/70' : 'text-gray-500'}`}>
                                    ({(attachment.size / 1024).toFixed(1)} KB)
                                  </span>
                                </div>
                                <button
                                  onClick={() => handleFileAttachmentDownload(attachment)}
                                  className={`flex items-center space-x-1 px-2 py-1 rounded text-xs transition-colors ${
                                    message.isOwnMessage
                                      ? 'bg-white/20 hover:bg-white/30 text-white'
                                      : 'bg-blue-50 hover:bg-blue-100 text-blue-600'
                                  }`}
                                >
                                  <Download className="w-3 h-3" />
                                  <span>Download</span>
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {/* Initials for own messages */}
                        {message.isOwnMessage && message.actualSenderInitials && (
                          <div className="flex items-center justify-end mt-1">
                            <div 
                              className="group relative"
                              title={`Sent by ${message.displayName}`}
                            >
                              <span className="inline-flex items-center justify-center w-4 h-4 text-xs font-medium text-blue-200 bg-blue-600 rounded-full hover:bg-blue-500 transition-colors cursor-help">
                                {message.actualSenderInitials}
                              </span>
                              {/* Tooltip */}
                              <div className="absolute bottom-full right-0 mb-2 px-2 py-1 text-xs text-white bg-gray-800 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
                                Sent by {message.displayName}
                              </div>
                            </div>
                          </div>
                        )}
                        
                        <div className="flex items-center justify-between mt-2">
                          <div className="flex items-center gap-2">
                            <span className="text-xs opacity-75">
                              {formatTimestamp(message.timestamp)}
                            </span>
                            {/* Read Receipt for Own Messages */}
                            {message.isOwnMessage && (
                              <div className="flex items-center gap-1 text-xs opacity-75">
                                {message.isRead || message.is_read ? (
                                  <>
                                    <CheckCheck className="w-3 h-3 text-blue-400" />
                                    <span className="text-blue-400">Read</span>
                                  </>
                                ) : (
                                  <Check className="w-3 h-3" />
                                )}
                              </div>
                            )}
                          </div>
                          {message.isOwnMessage && (() => {
                            // Check if message is within 15-minute deletion window
                            const messageTime = new Date(message.timestamp);
                            const now = new Date();
                            const minutesSinceCreation = (now - messageTime) / (1000 * 60);
                            const canDelete = minutesSinceCreation <= 15;
                            
                            return (
                              <button
                                onClick={() => {
                                  if (canDelete) {
                                    handleDeleteMessage(message.id);
                                  } else {
                                    alert('Messages can only be deleted within 15 minutes of sending');
                                  }
                                }}
                                disabled={!canDelete}
                                className={`text-xs transition-opacity duration-200 ${
                                  canDelete 
                                    ? 'opacity-75 hover:opacity-100 cursor-pointer' 
                                    : 'opacity-30 cursor-not-allowed'
                                }`}
                                title={canDelete ? 'Delete message' : `Can only delete within 15 minutes (${Math.floor(minutesSinceCreation)} min ago)`}
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>

              {/* Message Input */}
              <div className="border-t border-gray-200 p-4">
                {/* Attachments Preview */}
                {attachments.length > 0 && (
                  <div className="mb-3 p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">Attachments:</span>
                      <button
                        onClick={() => setAttachments([])}
                        className="text-sm text-red-600 hover:text-red-800"
                      >
                        Clear All
                      </button>
                    </div>
                    <div className="space-y-2">
                      {attachments.map((attachment) => (
                        <div key={attachment.id} className="flex items-center justify-between p-2 bg-white rounded border">
                          <div className="flex items-center space-x-2">
                            <FileText className="w-4 h-4 text-gray-500" />
                            <span className="text-sm text-gray-700">{attachment.name}</span>
                            <span className="text-xs text-gray-500">
                              ({(attachment.size / 1024).toFixed(1)} KB)
                            </span>
                          </div>
                          <button
                            onClick={() => removeAttachment(attachment.id)}
                            className="p-1 text-red-500 hover:text-red-700"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Input Area with Drag & Drop */}
                <div 
                  className={`flex items-end space-x-3 relative ${
                    isDragging ? 'ring-2 ring-blue-500 rounded-lg' : ''
                  }`}
                  onDragEnter={handleDragEnter}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  {isDragging && (
                    <div className="absolute inset-0 bg-blue-50 border-2 border-dashed border-blue-500 rounded-lg flex items-center justify-center z-10 pointer-events-none">
                      <div className="text-center">
                        <Paperclip className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                        <p className="text-blue-600 font-medium">Drop files here</p>
                        <p className="text-blue-500 text-sm">Max 10MB per file</p>
                      </div>
                    </div>
                  )}
                  <div className="flex-1">
                    <textarea
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Type your message or drag files here..."
                      rows="3"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSendMessage();
                        }
                      }}
                    />
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                      title="Attach File"
                    >
                      <Paperclip className="w-5 h-5" />
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      onChange={handleFileUpload}
                      className="hidden"
                      accept=".pdf,.xlsx,.xls,.doc,.docx"
                    />
                    <button
                      onClick={handleSendMessage}
                      disabled={!newMessage.trim() && attachments.length === 0}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors duration-200 flex items-center space-x-2"
                    >
                        <Send className="w-4 h-4" />
                      <span>Send</span>
                    </button>
                  </div>
                </div>
              </div>
            </>
          ) : (
            /* Empty State */
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <MessageSquare className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No conversation selected</h3>
                <p className="text-gray-500">Choose a conversation from the list to start messaging</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* New Chat Modal */}
      {showNewChat && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-gray-900">Start Invoice Conversation</h3>
                <button
                  onClick={() => {
                    console.log('Closing new chat modal');
                    setShowNewChat(false);
                    // Reset form
                    setNewChatInvoiceId('');
                    setNewChatMessage('');
                    setSelectedInvoiceForChat(null);
                    setIsGuestUser(false);
                    setInvoiceSearchQuery('');
                    setSearchResults([]);
                    setInvoiceSearchQuery('');
                    setSearchResults([]);
                  }}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-6">
                {/* Header Info */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Start Invoice Conversation
                  </label>
                  <div className="text-xs text-gray-500 bg-blue-50 p-3 rounded-lg">
                    💡 All conversations are invoice-based. Search for an invoice you've sent or received to start discussing it.
                  </div>
                </div>

                {/* Search Input */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Search Invoices *
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={invoiceSearchQuery}
                      onChange={(e) => {
                        const value = e.target.value;
                        setInvoiceSearchQuery(value);
                        
                        // Clear previous timeout
                        if (searchTimeoutRef.current) {
                          clearTimeout(searchTimeoutRef.current);
                        }
                        
                        if (value.trim()) {
                          // Debounce search to avoid too many API calls
                          searchTimeoutRef.current = setTimeout(() => {
                            searchInvoices(value);
                          }, 300);
                        } else {
                          setSearchResults([]);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          searchInvoices(invoiceSearchQuery);
                        }
                      }}
                      placeholder="Search by invoice number, customer name, or company..."
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-10"
                    />
                    <button
                      onClick={() => {
                        if (invoiceSearchQuery.trim()) {
                          searchInvoices(invoiceSearchQuery);
                        }
                      }}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
                      type="button"
                    >
                      <Search className="w-4 h-4" />
                    </button>
                    {searching && (
                      <div className="absolute right-12 top-1/2 transform -translate-y-1/2">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                      </div>
                    )}
                  </div>
                  
                  {/* Search Results */}
                  {searchResults.length > 0 ? (
                    <div className="mt-2 max-h-48 overflow-y-auto border border-gray-200 rounded-lg">
                      {searchResults.map((invoice) => (
                        <div
                          key={invoice.id}
                          onClick={() => handleInvoiceSelection(invoice)}
                          className="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="font-medium text-gray-900">{invoice.invoiceNumber}</div>
                              <div className="text-sm text-gray-600">
                                {invoice.receiver?.companyName || `${invoice.receiver?.firstName} ${invoice.receiver?.lastName}`}
                              </div>
                              <div className="text-xs text-gray-500">
                                {formatAmountLocale(invoice.total, invoice.currency)} • {invoice.status}
                              </div>
                            </div>
                            <div className="text-xs text-gray-400 ml-2">
                              {invoice.senderId === accountUserId ? 'Sent' : 'Received'}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : invoiceSearchQuery.trim() && !searching ? (
                    <div className="mt-2 p-3 text-center text-gray-500 border border-gray-200 rounded-lg">
                      <p>No invoices found for "{invoiceSearchQuery}"</p>
                      <p className="text-sm mt-1">All matching invoices already have chats, or no invoices match your search.</p>
                    </div>
                  ) : null}

                  {/* Selected Invoice Display */}
                  {selectedInvoiceForChat && (
                    <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="font-medium text-blue-900">{selectedInvoiceForChat.invoiceNumber}</div>
                          <div className="text-sm text-blue-700">
                            {selectedInvoiceForChat.receiver?.companyName || `${selectedInvoiceForChat.receiver?.firstName} ${selectedInvoiceForChat.receiver?.lastName}`}
                          </div>
                          <div className="text-xs text-blue-600">
                            {selectedInvoiceForChat.currency} {selectedInvoiceForChat.total} • {selectedInvoiceForChat.status}
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            setSelectedInvoiceForChat(null);
                            setNewChatInvoiceId('');
                            setIsGuestUser(false);
                          }}
                          className="text-blue-500 hover:text-blue-700"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {/* Guest User Warning */}
                  {isGuestUser && (
                    <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <div className="flex items-start space-x-2">
                        <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                        <div>
                          <div className="text-sm font-medium text-yellow-800">
                            Guest User Detected
                          </div>
                          <div className="text-sm text-yellow-700 mt-1">
                            This invoice was sent to a guest user. Guest users do not have access to the chat system, so messages cannot be sent to them.
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Initial Message */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Initial Message *
                  </label>
                  <textarea
                    value={newChatMessage}
                    onChange={(e) => setNewChatMessage(e.target.value)}
                    placeholder="Type your first message to start the conversation..."
                    rows={4}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    required
                  />
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    onClick={() => {
                      setShowNewChat(false);
                      // Reset form
                      setNewChatInvoiceId('');
                      setNewChatMessage('');
                      setSelectedInvoiceForChat(null);
                      setIsGuestUser(false);
                      setInvoiceSearchQuery('');
                      setSearchResults([]);
                    }}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreateNewChat}
                    disabled={!newChatInvoiceId || !newChatMessage.trim() || creatingChat || isGuestUser}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors duration-200 flex items-center space-x-2"
                  >
                    {creatingChat ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    ) : (
                      <MessageSquare className="w-4 h-4" />
                    )}
                    <span>
                      {creatingChat ? 'Creating...' : 
                       isGuestUser ? 'Cannot Chat with Guest' : 'Start Chat'}
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Invoice Preview Modal */}
      <InvoicePreview
        invoice={transformInvoiceForPreview(activeConversation?.invoice, activeConversation)}
        isOpen={showInvoicePreview}
        onClose={() => setShowInvoicePreview(false)}
        onDownloadPDF={(invoice) => handleDownload('pdf', invoice, { forcePrintablePreview: true })}
        onDownloadExcel={(invoice) => handleDownload('excel', invoice)}
        showDownloadButtons={true}
        title="Invoice Details"
      />
    </div>
  );
};

export default Chat;
